import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, X, AlertCircle } from 'lucide-react';

export interface AutocompleteOption<T = string> {
  value: T;
  label: string;
  /** Optional secondary text (e.g. description) */
  secondary?: string;
}

export interface AutocompleteProps<T = string> {
  /** Options to show. Use { value, label } or string (value=label) */
  options: AutocompleteOption<T>[] | string[];
  value: T | '';
  onChange: (value: T | '') => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  /** Allow clearing the selection */
  clearable?: boolean;
  /** Filter options as user types (default: true) */
  filterable?: boolean;
  /** Allow custom value not in options (e.g. for Product Name) */
  freeSolo?: boolean;
  /** Get display label for an option (for object options) */
  getOptionLabel?: (option: AutocompleteOption<T> | string) => string;
  /** Compare option to value for selection */
  isOptionEqualToValue?: (option: AutocompleteOption<T> | string, value: T | '') => boolean;
  /** Hint text above dropdown */
  hint?: string;
  error?: string;
  /** ARIA and test IDs */
  'data-testid'?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  id?: string;
  /** Optional class for the container */
  className?: string;
}

function normalizeOptions<T>(options: AutocompleteOption<T>[] | string[]): AutocompleteOption<T>[] {
  return options.map((opt) =>
    typeof opt === 'string' ? ({ value: opt as T, label: opt } as AutocompleteOption<T>) : opt
  );
}

function defaultGetOptionLabel<T>(opt: AutocompleteOption<T> | string): string {
  return typeof opt === 'string' ? opt : opt.label;
}

function defaultIsOptionEqualToValue<T>(
  opt: AutocompleteOption<T> | string,
  value: T | ''
): boolean {
  const optVal = typeof opt === 'string' ? opt : opt.value;
  return optVal === value;
}

export function Autocomplete<T = string>({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label,
  disabled = false,
  loading = false,
  clearable = true,
  filterable = true,
  freeSolo = false,
  getOptionLabel = defaultGetOptionLabel,
  isOptionEqualToValue = defaultIsOptionEqualToValue,
  hint,
  error,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  id,
  className = '',
}: AutocompleteProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const clearingRef = useRef(false);

  const normalizedOptions = normalizeOptions(options);
  const selectedOption = normalizedOptions.find((opt) => isOptionEqualToValue(opt, value));
  const displayValue = selectedOption
    ? getOptionLabel(selectedOption)
    : value !== ''
      ? String(value)
      : '';

  const filteredOptions = useCallback(() => {
    if (!filterable || !inputValue.trim()) return normalizedOptions;
    const q = inputValue.toLowerCase();
    return normalizedOptions.filter((opt) =>
      getOptionLabel(opt).toLowerCase().includes(q)
    );
  }, [normalizedOptions, inputValue, filterable, getOptionLabel]);

  const optionsToShow = filteredOptions();

  useEffect(() => {
    if (!isOpen) {
      setInputValue(displayValue);
      setActiveIndex(-1);
    }
  }, [isOpen, displayValue]);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && listRef.current) {
      const el = listRef.current.children[activeIndex] as HTMLElement;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, activeIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    setIsOpen(true);
    setActiveIndex(0);
    if (!v) onChange('' as T | '');
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setInputValue(displayValue);
    setActiveIndex(-1);
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (clearingRef.current) {
        clearingRef.current = false;
        setIsOpen(false);
        return;
      }
      if (freeSolo && inputValue.trim() && inputValue.trim() !== displayValue) {
        onChange(inputValue.trim() as T);
      }
      setIsOpen(false);
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (i < optionsToShow.length - 1 ? i + 1 : i));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (i > 0 ? i - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && optionsToShow[activeIndex]) {
          onChange(optionsToShow[activeIndex].value);
          setIsOpen(false);
        } else if (freeSolo && inputValue.trim()) {
          onChange(inputValue.trim() as T);
          setIsOpen(false);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setInputValue(displayValue);
        setActiveIndex(-1);
        break;
    }
  };

  const handleSelect = (opt: AutocompleteOption<T>) => {
    onChange(opt.value);
    setIsOpen(false);
    setInputValue(getOptionLabel(opt));
    inputRef.current?.blur();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearingRef.current = true;
    onChange('' as T | '');
    setInputValue('');
    setIsOpen(false);
  };

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={isOpen ? inputValue : displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={id ? `${id}-listbox` : undefined}
          aria-activedescendant={
            activeIndex >= 0 && id ? `${id}-option-${activeIndex}` : undefined
          }
          aria-label={ariaLabel ?? label}
          aria-invalid={ariaInvalid ?? !!error}
          aria-describedby={ariaDescribedBy}
          data-testid={dataTestId}
          className={`w-full px-3 py-3 pr-16 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm ${
            error ? 'border-red-300' : 'border-gray-200'
          } ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
        />
        <div className={`absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5`}>
          {clearable && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              onMouseDown={(e) => e.preventDefault()}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              aria-label="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <ChevronDown
            className={`z-10 w-4 h-4 text-gray-400 transition-transform pointer-events-none ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        {isOpen && !disabled && (
          <>
            <div
              className="fixed inset-0 z-10"
              aria-hidden="true"
              onClick={() => setIsOpen(false)}
            />
            <div
              ref={listRef}
              id={id ? `${id}-listbox` : undefined}
              role="listbox"
              aria-label={label ?? 'Options'}
              className="absolute left-0 right-0 z-40 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto scrollbar-thin min-w-0"
            >
            {loading ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center z-20">
                Loading...
              </div>
            ) : optionsToShow.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center z-20">
                {freeSolo && inputValue.trim()
                  ? `Press Enter to use "${inputValue.trim()}"`
                  : 'No options found'}
              </div>
            ) : (
              optionsToShow.map((opt, index) => (
                <button
                  key={`${opt.value}-${index}`}
                  type="button"
                  id={id ? `${id}-option-${index}` : undefined}
                  role="option"
                  aria-selected={index === activeIndex || isOptionEqualToValue(opt, value)}
                  onClick={() => handleSelect(opt)}
                  data-testid={dataTestId ? `${dataTestId}-option-${opt.value}` : undefined}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex flex-col ${
                    index === activeIndex ? 'bg-gray-50' : ''
                  }`}
                >
                  <span className="text-gray-700">{opt.label}</span>
                  {opt.secondary && (
                    <span className="text-xs text-gray-500 mt-0.5">{opt.secondary}</span>
                  )}
                </button>
              ))
            )}
            </div>
          </>
        )}
      </div>

      {hint && !error && (
        <p className="mt-1 text-xs text-gray-500">{hint}</p>
      )}
      {error && (
        <p
          id={ariaDescribedBy}
          className="mt-1 text-xs text-red-600 flex items-center gap-1"
          role="alert"
        >
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}
